// ---------------------------------------------------------------------------
// parseGraphExport — turns a Naumu space-export (v2.0) JSON into the minimal,
// render-ready shape the landing graph needs. Pure + defensive: tolerates the
// loose `Record<string, unknown>` rows the export emits, drops malformed rows
// and dangling edges, and splits edges into parent (backbone) vs mesh.
//
// No global side effects — schema type colors are returned (not pushed into the
// app-wide getColorForType override map), so the marketing graph stays isolated.
// ---------------------------------------------------------------------------

import type {
	MarketingGraphEdge,
	MarketingGraphNode,
	ParsedMarketingGraph,
} from './types';

interface RawExport {
	nodes?: ReadonlyArray<Record<string, unknown>>;
	edges?: ReadonlyArray<Record<string, unknown>>;
	schema?: { nodes?: ReadonlyArray<{ type?: unknown; color?: unknown }> };
}

const asString = (v: unknown): string | undefined =>
	typeof v === 'string' ? v : undefined;

// Accepts `unknown` so an `as const` export literal (deeply readonly) slots in
// without fighting the type checker — every field is re-validated below anyway.
export const parseGraphExport = (input: unknown): ParsedMarketingGraph => {
	const raw = (input ?? {}) as RawExport;
	const rawNodes = Array.isArray(raw.nodes) ? raw.nodes : [];
	const rawEdges = Array.isArray(raw.edges) ? raw.edges : [];

	const nodes: MarketingGraphNode[] = [];
	for (const n of rawNodes) {
		const id = asString(n.id);
		if (!id) continue;
		nodes.push({
			id,
			label: asString(n.label) ?? asString(n.name) ?? id,
			type: asString(n.type) ?? 'Node',
			content: asString(n.content),
		});
	}

	const nodeIds = new Set(nodes.map((node) => node.id));

	const parentEdges: MarketingGraphEdge[] = [];
	const meshEdges: MarketingGraphEdge[] = [];
	for (const e of rawEdges) {
		const source = asString(e.source);
		const target = asString(e.target);
		// Drop dangling / self edges — they can't be laid out or revealed.
		if (!source || !target || source === target) continue;
		if (!nodeIds.has(source) || !nodeIds.has(target)) continue;
		const edge: MarketingGraphEdge = {
			source,
			target,
			isParent: e.isParent === true,
			label: asString(e.label),
		};
		(edge.isParent ? parentEdges : meshEdges).push(edge);
	}

	const schemaColorsByType = new Map<string, string>();
	for (const sn of raw.schema?.nodes ?? []) {
		const type = asString(sn?.type);
		const color = asString(sn?.color);
		if (type && color) schemaColorsByType.set(type, color);
	}

	return { nodes, parentEdges, meshEdges, schemaColorsByType };
};
